/* ===========================================================
   panels/History.jsx — Version history (Casual) / History (Developer)

   Save a version with a name · versions saved automatically while you
   work · preview any version · restore it (the doc as it is now is
   saved first, so a restore can be undone) · compare any version with
   now · try an edit on a copy, compare it, merge it back.
   =========================================================== */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { listVersions, saveVersion, loadVersion } from '../sync/versions.js';
import { toJSON, applyJSON, normalize } from '../editor/convert.js';
import { createDoc, findDoc } from '../sync/docs.js';
import { docs, online, mode } from '../state.js';
import { go } from '../lib/router.js';
import { Panel } from './Share.jsx';
import { compareView, Html } from './Compare.jsx';
import { mergeDialog, openHeadless } from './Merge.jsx';
import { Button, Icon, IconButton, Spinner, Dialog, openModal, promptDialog, confirmDialog, toast, timeAgo, bytes } from '../ui/ui.jsx';
import { ProtoCard } from '../ui/proto.jsx';
import { loadKatex } from '../lib/katex.js';
import { w } from '../lib/words.js';

const KIND = {
  named: null, auto: 'auto', restore: 'restore', copy: 'copy', merge: 'merge', import: 'import',
};

function dayOf(t) {
  const d = new Date(t);
  const today = new Date();
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}

function labelOf(v) {
  if (v.name) return v.name;
  if (v.kind === 'auto') return w('auto');
  if (v.kind === 'restore') return w('restored');
  if (v.kind === 'copy') return `${w('copy')} made`;
  if (v.kind === 'merge') return 'Merged changes';
  return 'Version';
}

/** "Try an edit on a copy": a starting point is saved, then a private copy opens. */
export async function makeEditCopy({ id, sync, title }) {
  const json = toJSON(sync.doc);
  let baseVersion = null;
  if (online.value && sync.synced) {
    try { baseVersion = (await saveVersion(id, sync.doc, { kind: 'copy', name: `Before ${w('copy')}` })).id; } catch { /* the copy carries its own starting point anyway */ }
  }
  const copyId = await createDoc({ title: `${title || 'Untitled document'} (${w('copy')})`, json, kind: 'copy', parentId: id, baseVersion, base: json });
  go(`/d/${copyId}`);
  toast(mode.value === 'dev' ? 'Branch created. Merge it back from History when ready.' : 'Here\'s your copy. Change anything; when you like it, use "Merge changes back".');
}

export function HistoryPanel({ id, sync, meta, title, canEdit, onClose }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState(null);
  const [onlyNamed, setOnlyNamed] = useState(false);
  const [busy, setBusy] = useState(false);
  const dev = mode.value === 'dev';

  const load = () => listVersions(id).then(r => setList(r.versions)).catch(setErr);
  useEffect(() => { if (online.value) load(); }, [id, online.value]);

  const copies = docs.value.filter(d => d.kind === 'copy' && d.parentId === id && !d.deletedAt);
  const isCopy = meta?.kind === 'copy';

  const saveNamed = async () => {
    const name = await promptDialog({ title: w('saveVersion'), label: w('versionName'), placeholder: w('versionNamePlaceholder'), hint: w('saveVersionHint'), ok: 'Save' });
    if (name === null) return;
    setBusy(true);
    try {
      await sync.flushLocal();
      await saveVersion(id, sync.doc, { kind: 'named', name: name.trim() || 'Saved version' });
      toast('Version saved.');
      load();
    } catch (e) { toast(e, { kind: 'error' }); } finally { setBusy(false); }
  };

  const restore = async v => {
    if (!await confirmDialog({ title: 'Restore this version?', text: `The doc will go back to "${labelOf(v)}" (${new Date(v.at).toLocaleString()}). The way it is now gets saved as a version first, so you can undo this.`, ok: 'Restore' })) return;
    setBusy(true);
    try {
      const ydoc = await loadVersion(id, v.id);
      await saveVersion(id, sync.doc, { kind: 'restore', name: 'Before restoring' });
      // every tab comes back as it was in that version (tabs added since are emptied)
      applyJSON(sync.doc, toJSON(ydoc), 'dp:restore');
      const then = ydoc.getArray('tabs').toArray(), now = sync.doc.getArray('tabs').toArray();
      if (then.length || now.length) {
        sync.doc.transact(() => {
          for (const t of then) if (t.id !== 'default') applyJSON(sync.doc, toJSON(ydoc, `tab-${t.id}`), 'dp:restore', `tab-${t.id}`);
          const arr = sync.doc.getArray('tabs');
          arr.delete(0, arr.length);
          if (then.length) arr.insert(0, then);
        }, 'dp:restore');
      }
      await sync.flushLocal();
      setTimeout(() => saveVersion(id, sync.doc, { kind: 'restore', name: `Restored "${labelOf(v)}"` }).then(load).catch(() => {}), 1500);
      toast('Restored.');
    } catch (e) { toast(e, { kind: 'error' }); } finally { setBusy(false); }
  };

  const preview = async v => {
    try {
      const [ydoc] = await Promise.all([loadVersion(id, v.id), loadKatex().catch(() => {})]);
      const json = toJSON(ydoc);
      openModal(done => (
        <Dialog title={labelOf(v)} wide onClose={() => done()}
          actions={<>
            <span class="hint">{new Date(v.at).toLocaleString()} · {v.authorName || 'Someone'}</span>
            <span class="grow" />
            <Button icon="compare" onClick={() => { done(); compareView({ left: json, right: toJSON(sync.doc), leftLabel: labelOf(v), rightLabel: w('now') }); }}>{w('compareNow')}</Button>
            {canEdit && <Button kind="primary" icon="restore" onClick={() => { done(); restore(v); }}>{w('restore')}</Button>}
          </>}>
          <div class="version-preview"><Html node={json} /></div>
        </Dialog>
      ));
    } catch (e) { toast(e, { kind: 'error' }); }
  };

  const compare = async v => {
    try {
      const [ydoc] = await Promise.all([loadVersion(id, v.id), loadKatex().catch(() => {})]);
      compareView({ left: toJSON(ydoc), right: toJSON(sync.doc), leftLabel: labelOf(v), rightLabel: w('now') });
    } catch (e) { toast(e, { kind: 'error' }); }
  };

  const compareCopy = async copyId => {
    const other = await openHeadless(copyId);
    try { compareView({ left: toJSON(sync.doc), right: toJSON(other.doc), leftLabel: w('original'), rightLabel: findDoc(copyId)?.title || w('copy') }); }
    finally { other.destroy(); }
  };

  const compareWithOriginal = async () => {
    const orig = await openHeadless(meta.parentId);
    try { compareView({ left: toJSON(orig.doc), right: toJSON(sync.doc), leftLabel: w('original'), rightLabel: `This ${w('copy')}` }); }
    finally { orig.destroy(); }
  };

  const shown = (list || []).filter(v => !onlyNamed || v.kind === 'named');
  const groups = useMemo(() => {
    const out = [];
    for (const v of shown) {
      const day = dayOf(v.at);
      if (!out.length || out[out.length - 1].day !== day) out.push({ day, items: [] });
      out[out.length - 1].items.push(v);
    }
    return out;
  }, [list, onlyNamed]);

  return (
    <Panel title={w('history')} onClose={onClose}>
      {isCopy && (
        <div class="copy-box">
          <p><Icon name="branch" size={16} /> This is an {w('copy')}{findDoc(meta.parentId) ? <> of <a href={`/d/${meta.parentId}`}>{findDoc(meta.parentId).title || 'Untitled document'}</a></> : ''}.</p>
          <div class="row-gap">
            <Button size="sm" icon="compare" onClick={compareWithOriginal}>{w('compare')} with {w('original').toLowerCase()}</Button>
            <Button size="sm" kind="primary" icon="merge" onClick={() => mergeDialog({ copyId: id, copySync: sync })}>{w('mergeBack')}</Button>
          </div>
        </div>
      )}

      {canEdit && (
        <div class="row-gap">
          <Button kind="primary" icon="check" onClick={saveNamed} disabled={busy || !online.value}>{w('saveVersion')}</Button>
          {!isCopy && <Button icon="branch" onClick={() => makeEditCopy({ id, sync, title })}>{w('tryCopy')}</Button>}
        </div>
      )}
      {!isCopy && canEdit && <p class="hint">{w('tryCopyHint')}</p>}

      {copies.length > 0 && (
        <>
          <h3 class="panel-sub">{w('copies')}</h3>
          <ul class="copies">
            {copies.map(c => (
              <li key={c.id}>
                <a href={`/d/${c.id}`} class="grow">{c.title || 'Untitled'}{c.mergedAt ? <span class="tag">merged</span> : null}</a>
                <IconButton icon="compare" label={w('compare')} onClick={() => compareCopy(c.id)} />
                {canEdit && <IconButton icon="merge" label={w('mergeBack')} onClick={() => mergeDialog({ copyId: c.id, origSync: sync })} />}
              </li>
            ))}
          </ul>
        </>
      )}

      <div class="history-head">
        <h3 class="panel-sub">{dev ? 'Commits' : 'Versions'}</h3>
        <label class="check small"><input type="checkbox" checked={onlyNamed} onChange={e => setOnlyNamed(e.target.checked)} /><span>Only named</span></label>
      </div>
      {!online.value ? <ProtoCard small title="History needs the internet" text="Your edits are still being saved on this device. Saved versions show up here when you're back online." />
        : err ? <ProtoCard small error={err} />
          : !list ? <Spinner /> : !shown.length ? <p class="hint">No versions yet. They're saved automatically every few minutes while you edit, or press "{w('saveVersion')}".</p> : (
            groups.map(g => (
              <div class="vgroup" key={g.day}>
                <h4>{g.day}</h4>
                <ul class="versions">
                  {g.items.map(v => (
                    <li key={v.id} class={v.kind === 'named' ? 'named' : ''}>
                      <button type="button" class="version" onClick={() => preview(v)}>
                        <span class="version-name">{labelOf(v)}</span>
                        <span class="version-meta">
                          {new Date(v.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · {v.authorName || 'Someone'}
                          {dev && <> · <code>{v.id.slice(0, 7)}</code> · {bytes(v.size)}{v.checkpoint ? ' · full' : ' · Δ'}</>}
                        </span>
                      </button>
                      <IconButton icon="compare" label={w('compareNow')} onClick={() => compare(v)} />
                      {canEdit && <IconButton icon="restore" label={w('restore')} onClick={() => restore(v)} />}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
    </Panel>
  );
}
